import React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import Button, { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import { Flex, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { getSourceIconStack } from './getSourceIcon.tsx';
import { SourceItem } from './SourceItem.tsx';
import type { Source } from './popularQuestionsTypes.ts';

export const SourcesSection = ({
	sources,
	isExpanded,
	onToggle,
}: {
	sources: Source[];
	isExpanded: boolean;
	onToggle: () => void;
}) => {
	const { formatMessage } = useIntl();

	if (sources.length === 0) {
		return null;
	}

	return (
		<div css={styles.sectionLabel}>
			<Inline spread="space-between">
				<Button onClick={onToggle}>
					<Flex gap="space.075" alignItems="center">
						{getSourceIconStack(sources)}
						<Text size="small" color="color.text.subtle">
							{formatMessage(i18n.sourcesCount, {
								count: sources.length,
							})}
						</Text>
					</Flex>
				</Button>
				<IconButton
					icon={isExpanded ? ChevronDownIcon : ChevronRightIcon}
					label={formatMessage(isExpanded ? i18n.collapseSources : i18n.expandSources)}
					appearance="subtle"
					onClick={onToggle}
				/>
			</Inline>
			<div css={styles.sourcesDivider} />
			{isExpanded && (
				<>
					<Stack space="space.200">
						{sources.map((source) => (
							<SourceItem key={source.url} source={source} />
						))}
					</Stack>
					<div css={styles.sourcesDivider} />
				</>
			)}
		</div>
	);
};

const i18n = defineMessages({
	sourcesCount: {
		id: 'space-overview.smart-space-overview.popular-questions.sources-count',
		defaultMessage: '{count, plural, one {# Source} other {# Sources}}',
		description: 'Button label showing the number of sources for a popular question.',
	},
	expandSources: {
		id: 'space-overview.smart-space-overview.popular-questions.expand-sources',
		defaultMessage: 'Expand sources',
		description: 'Accessible label for the button to expand sources.',
	},
	collapseSources: {
		id: 'space-overview.smart-space-overview.popular-questions.collapse-sources',
		defaultMessage: 'Collapse sources',
		description: 'Accessible label for the button to collapse sources.',
	},
});

const styles = cssMap({
	sectionLabel: {
		paddingBlockEnd: token('space.050'),
	},
	sourcesDivider: {
		borderTopStyle: 'solid',
		borderTopWidth: token('border.width'),
		borderTopColor: token('color.border'),
		marginBlock: token('space.100'),
	},
});
